import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Plus,
    Building2,
    Clock,
    Trash2,
    Edit,
    CheckCircle2,
    XCircle,
    User
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

const FacilityManager: React.FC = () => {
    const [facilities, setFacilities] = useState<any[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        rules: '',
        hourly_rate: 0,
        max_capacity: 10,
        requires_approval: true
    });

    useEffect(() => { fetchFacilities(); }, []);

    const fetchFacilities = async () => {
        const res = await api.get('/facilities');
        setFacilities(res.data);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/facilities', formData);
            setIsAdding(false);
            fetchFacilities();
            setFormData({ name: '', description: '', rules: '', hourly_rate: 0, max_capacity: 10, requires_approval: true });
        } catch (e) { alert('Action failed'); }
    };

    const toggleActive = async (id: string, current: boolean) => {
        // Simple mock of toggle since we don't have PATCH logic fully for is_active yet
        // In real app, call PATCH /facilities/{id}
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Facility Management" subtitle="Configure bookable society amenities">
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isAdding ? 'Cancel' : 'Add Facility'}
                    <Plus size={18} className={cn("transition-transform", isAdding && "rotate-45")} />
                </button>
            </PageHeader>

            {isAdding && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Facility Name</label>
                                <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="e.g. Community Hall" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Hourly Rate (₹)</label>
                                <input type="number" value={formData.hourly_rate} onChange={e => setFormData({...formData, hourly_rate: parseFloat(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Max Capacity</label>
                                <input type="number" value={formData.max_capacity} onChange={e => setFormData({...formData, max_capacity: parseInt(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="flex items-end pb-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={formData.requires_approval} onChange={e => setFormData({...formData, requires_approval: e.target.checked})} className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                                    <span className="text-sm font-medium text-slate-700">Requires Admin Approval</span>
                                </label>
                            </div>
                            <div className="md:col-span-2 space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Rules & Guidelines</label>
                                <textarea value={formData.rules} onChange={e => setFormData({...formData, rules: e.target.value})} rows={2} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">
                                Save Facility
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {facilities.map((f) => (
                    <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col hover:border-violet-200 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600">
                                <Building2 size={20} />
                            </div>
                            <div className={cn(
                                "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                f.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-red-50 text-red-700 border-red-100"
                            )}>
                                {f.is_active ? 'Active' : 'Inactive'}
                            </div>
                        </div>

                        <h4 className="font-bold text-slate-900 text-lg">{f.name}</h4>
                        <p className="text-xs text-slate-500 mt-1 flex-1">{f.description || 'No description provided'}</p>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Rate</p>
                                <p className="text-sm font-bold text-slate-700">₹{f.hourly_rate}/hr</p>
                            </div>
                            <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Capacity</p>
                                <p className="text-sm font-bold text-slate-700">{f.max_capacity} Pax</p>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-50 flex justify-between items-center">
                            <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400">
                                {f.requires_approval ? <CheckCircle2 size={12} className="text-emerald-500" /> : <XCircle size={12} className="text-slate-300" />}
                                Approval Needed
                            </div>
                            <div className="flex gap-2">
                                <button className="p-1.5 text-slate-400 hover:text-violet-600"><Edit size={16} /></button>
                                <button className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FacilityManager;
